import { Logger } from '@quickplay/foundation';
import { AdComposedPlayer, PLAYBACK_STATES, Player, PlayerContext } from '@quickplay/player-interface';
import { BrightcoveDataCollectionAdapter } from '../model/BrightcoveDataCollectionAdapter';
import { BrightcoveDataCollectionConfig } from '../model/BrightcoveDataCollectionConfig';
import { BrightcoveDataCollectionTracker } from './BrightcoveDataCollectionTracker';

/**
 * Default implementation of BrightcoveDataCollectionAdapter.
 *
 * Manages the lifecycle of Brightcove Data Collection API v2 events,
 * handling state tracking, metric collection, and periodic event reporting.
 * Subscribes to player events and manages all data collection logic internally.
 *
 * EVENT SEQUENCE (per Brightcove Data Collection API v2 spec):
 * 1. player_load - Sent on adapter initialization, marks beginning of session
 * 2. video_impression - Sent when video metadata loads (PLAYBACK_STATES.LOADED)
 *    At this point, player is ready but playback hasn't started (user hasn't clicked play)
 * 3. play_request - Sent when playback actually starts (PLAYBACK_STATES.STARTED)
 *    This is triggered by user action (manual play) or autoplay mechanism
 *    Only sent once per session per media stream (not on pause/resume)
 * 4. video_view - Sent on first progress update after playback starts
 *    Only sent once per viewing session even if user stops and restarts
 *
 * The gap between video_impression (metadata ready) and play_request (playback requested)
 * represents the user decision window, which is naturally provided by the player state machine.
 */
export class DefaultBrightcoveDataCollectionAdapter implements BrightcoveDataCollectionAdapter {
  private readonly tracker: BrightcoveDataCollectionTracker;
  private readonly config: BrightcoveDataCollectionConfig;
  private readonly player: Player;
  private readonly logger: Logger;
  private readonly engagementInterval: number;

  // State tracking
  private videoViewTracked = false;
  private playRequestTracked = false;
  private lastEngagementTime = 0;
  private engagementRangeStart = 0;
  private initialized = false;

  // For start_time_ms calculation
  private playRequestTime: number | null = null;
  private adBreaks: Array<{ start: number; end: number }> = [];
  private adBreakActive = false;
  private adBreakStartTime: number | null = null;

  // Track active variant from trackschanged event
  private activeVariantBitrate: number | undefined;
  private activeVariantMimeType: string | undefined;
  private activeVariantHeight: number | undefined;
  private activeVariantWidth: number | undefined;

  // Event handler references for cleanup
  private readonly boundHandlePlaybackStateChanged: (state: PLAYBACK_STATES) => void;
  private readonly boundHandleProgressUpdate: () => void;
  private readonly boundHandleError: (error: any) => void;
  private readonly boundHandlePlayerContextChanged: (context: PlayerContext) => void;
  private readonly boundHandleTracksChanged: () => void;

  constructor(config: BrightcoveDataCollectionConfig, player: Player, logger: Logger) {
    this.config = config;
    this.player = player;
    this.logger = logger;
    this.tracker = new BrightcoveDataCollectionTracker(config, logger);
    this.engagementInterval = config.engagementEventInterval ?? 10000; // Default 10 seconds

    // Bind event handlers once for proper cleanup
    this.boundHandlePlaybackStateChanged = this.handlePlaybackStateChanged.bind(this);
    this.boundHandleProgressUpdate = this.handleProgressUpdate.bind(this);
    this.boundHandleError = this.handleError.bind(this);
    this.boundHandlePlayerContextChanged = this.handlePlayerContextChanged.bind(this);
    this.boundHandleTracksChanged = this.handleTracksChanged.bind(this);

    // Initialize and start tracking
    this.initialize();
  }

  /**
   * Initialize the adapter and subscribe to all player events.
   */
  private initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Track initial player load
    this.onPlayerLoad();

    // Subscribe to player lifecycle events
    this.subscribeToPlayerEvents();
  }

  /**
   * Subscribe to all necessary player events for data collection.
   */
  private subscribeToPlayerEvents(): void {
    // Track playback state changes
    this.player.subscribe('playbackstatechanged', this.boundHandlePlaybackStateChanged);

    // Track progress updates for engagement
    this.player.subscribe('progressupdate', this.boundHandleProgressUpdate);

    // Track errors
    this.player.subscribe('error', this.boundHandleError);

    // Track variant/track changes for active variant info (only if QoE metrics enabled)
    if (this.config.includeQoEMetrics) {
      this.player.subscribe('trackschanged', this.boundHandleTracksChanged);
    }

    // Track player context changes (ad mode)
    if (this.isAdComposedPlayer(this.player)) {
      this.player.subscribe('playercontextchanged', this.boundHandlePlayerContextChanged);
    }
  }

  /**
   * Unsubscribe from all player events to prevent memory leaks.
   * Should be called when the adapter is no longer needed.
   */
  public dispose(): void {
    if (!this.initialized) {
      return;
    }

    // Unsubscribe from all events
    this.player.unsubscribe('playbackstatechanged', this.boundHandlePlaybackStateChanged);
    this.player.unsubscribe('progressupdate', this.boundHandleProgressUpdate);
    this.player.unsubscribe('error', this.boundHandleError);

    if (this.config.includeQoEMetrics) {
      this.player.unsubscribe('trackschanged', this.boundHandleTracksChanged);
    }

    if (this.isAdComposedPlayer(this.player)) {
      this.player.unsubscribe('playercontextchanged', this.boundHandlePlayerContextChanged);
    }

    // Clean up tracker resources
    this.tracker.destroy();

    this.initialized = false;
  }

  /**
   * Type guard to check if player is an AdComposedPlayer.
   */
  private isAdComposedPlayer(player: Player): player is AdComposedPlayer {
    return 'playerContext' in player;
  }

  /**
   * Check if a pre-roll ad is scheduled to play.
   * Pre-roll is indicated by a cue point of 0 in getCuePoints().
   * Returns true if pre-roll is scheduled and we're near the start of the video.
   */
  private hasScheduledPreroll(): boolean {
    try {
      const cuePoints = (this.player as AdComposedPlayer)?.getCuePoints();
      if (!Array.isArray(cuePoints)) {
        return false;
      }

      // Check if pre-roll (cue point 0) is scheduled
      const hasPreroll = cuePoints.includes(0);
      if (!hasPreroll) {
        return false;
      }

      // If pre-roll is scheduled and we're within the first 2 seconds,
      // assume pre-roll is about to play or is in the process of starting
      const currentTime = this.player.currentTime || 0;
      return currentTime < 2;
    } catch {
      // If we can't determine cue points, assume no pre-roll race condition
      return false;
    }
  }

  /**
   * Handle playback state changes.
   */
  private handlePlaybackStateChanged(playbackState: PLAYBACK_STATES): void {
    switch (playbackState) {
      case PLAYBACK_STATES.STARTED: {
        // Track play_request only once per session (first time playback starts)
        // Resuming from pause does not trigger a new play_request
        if (!this.playRequestTracked) {
          this.onPlayRequest();
        }
        break;
      }
      case PLAYBACK_STATES.LOADED: {
        // Track video impression when content is loaded
        this.onVideoImpression();
        break;
      }
      case PLAYBACK_STATES.IDLE:
      case PLAYBACK_STATES.STOPPED: {
        // Could add cleanup logic here if needed
        break;
      }
      default: {
        // Ignore other states
      }
    }
  }

  /**
   * Should be called when playback is requested (manual or autoplay).
   * Sends the mandatory play_request event.
   * Only sent once per session per media stream.
   */
  public onPlayRequest(): void {
    // Only send play_request once per session
    if (this.playRequestTracked) {
      return;
    }

    this.playRequestTracked = true;
    this.playRequestTime = Date.now();
    // Reset ad break tracking for new session
    this.adBreaks = [];
    this.adBreakActive = false;
    this.adBreakStartTime = null;
    // Send the mandatory play_request event
    this.tracker.trackPlayRequest();
  }

  /**
   * Handle progress updates during playback.
   */
  private handleProgressUpdate(): void {
    // Only track if we are SURE we are in main content (not ad)
    if (this.isAdComposedPlayer(this.player)) {
      if (this.player.playerContext === PlayerContext.MAIN) {
        // Avoid race condition: don't track video_view if pre-roll is scheduled
        // Pre-roll is indicated by a cue point of 0, and currentTime being near start
        if (this.hasScheduledPreroll()) {
          return;
        }

        // Track video_view on first progress update during content playback
        if (!this.videoViewTracked && this.player.playerState !== 'idle' && this.player.playerState !== 'stopped') {
          this.onVideoView();
        }
        // Track engagement periodically during content playback
        this.onVideoEngagement();
      }
    } else if (this.config.debugLogging) {
      this.logger.warn(
        '[DATA_COLLECTION] Skipping video_view/engagement: player is not AdComposedPlayer, cannot determine context.',
      );
    }
  }

  /**
   * Handle player context changes (for ad mode tracking).
   */
  private handlePlayerContextChanged(context: PlayerContext): void {
    if (context === PlayerContext.AD) {
      // Start ad break
      this.adBreakActive = true;
      this.adBreakStartTime = Date.now();
      this.onAdModeBegin();
    } else if (context === PlayerContext.MAIN) {
      // End ad break
      if (this.adBreakActive && this.adBreakStartTime !== null) {
        this.adBreaks.push({ start: this.adBreakStartTime, end: Date.now() });
      }
      this.adBreakActive = false;
      this.adBreakStartTime = null;
      this.onAdModeComplete();
    }
  }

  /**
   * Handle error events.
   */
  private handleError(error: any): void {
    const errorCode = error?.code || error?.message || 'UNKNOWN_ERROR';
    this.onError(errorCode);
  }

  /**
   * Handle track changes to update active variant information.
   */
  private handleTracksChanged(): void {
    try {
      const variantTracks = this.player?.rawPlayer?.getVariantTracks?.();
      if (!variantTracks || !Array.isArray(variantTracks)) {
        return;
      }

      const activeVariant = variantTracks.find((variant: any) => variant.active);
      if (activeVariant) {
        this.activeVariantBitrate = activeVariant.bitrate || activeVariant.bandwidth;
        this.activeVariantMimeType = activeVariant.mimeType;
        this.activeVariantHeight = activeVariant.height;
        this.activeVariantWidth = activeVariant.width;
      }
    } catch {
      if (this.config.debugLogging) {
        this.logger.log('[DATA_COLLECTION] Error extracting active variant from trackschanged');
      }
    }
  }

  /**
   * Called when the player is first initialized.
   * Sends the player_load event to mark the beginning of a tracking session.
   */
  private onPlayerLoad(): void {
    this.tracker.trackPlayerLoad();
  }

  /**
   * Called when video metadata is loaded and the player is ready for playback.
   * Sends the video_impression event.
   * Also resets play_request tracking for a new video.
   */
  private onVideoImpression(): void {
    // Reset play_request tracking when a new video is loaded
    this.playRequestTracked = false;
    this.videoViewTracked = false;
    this.tracker.trackVideoImpression();
  }

  /**
   * Called when actual video playback starts.
   * Sends the video_view event. Should only be called once per viewing session.
   */
  private onVideoView(): void {
    // Only send video_view once per session
    if (!this.videoViewTracked) {
      this.videoViewTracked = true;
      // Calculate start_time_ms: time from play_request to now, minus ad break durations
      let startTimeMs = 0;
      const now = Date.now();
      if (this.playRequestTime) {
        startTimeMs = now - this.playRequestTime;
        // Subtract ad break durations
        for (const ad of this.adBreaks) {
          startTimeMs -= ad.end - ad.start;
        }
      }
      this.tracker.trackVideoView(startTimeMs > 0 ? startTimeMs : undefined);
      this.lastEngagementTime = this.player.currentTime;
      this.engagementRangeStart = this.player.currentTime;
    }
  }

  /**
   * Called periodically during playback (every ~10 seconds by default).
   * Sends the video_engagement event with QoE metrics (if enabled).
   *
   * Only sends engagement for main content playback, not during ads.
   * Does not send if player is paused or stopped.
   *
   * @param force - If true, send engagement event even if interval has not elapsed
   */
  private onVideoEngagement(force?: boolean): void {
    // Only send engagement if in main content context (not during ads)
    if (this.isAdComposedPlayer(this.player) && this.player.playerContext !== PlayerContext.MAIN) {
      return;
    }

    // Do not send engagement if player is paused or stopped
    const state = this.player.playerState;
    if (state === PLAYBACK_STATES.PAUSED || state === PLAYBACK_STATES.STOPPED || state === PLAYBACK_STATES.IDLE) {
      return;
    }

    const now = this.player.currentTime;
    const engagementIntervalSeconds = this.engagementInterval / 1000;

    // Check if enough time has elapsed since last engagement event
    if (!force && now - this.lastEngagementTime < engagementIntervalSeconds) {
      return;
    }

    // Only send engagement events if video_view has been tracked
    if (!this.videoViewTracked) {
      return;
    }

    // Only extract QoE metrics if enabled (to avoid performance impact on low-end devices)
    const metrics = this.config.includeQoEMetrics ? this.extractQoEMetrics() : {};
    this.tracker.trackVideoEngagement(now, this.player.duration, this.engagementRangeStart, metrics);

    this.lastEngagementTime = now;
    this.engagementRangeStart = now;
  }

  /**
   * Called when ad playback begins (start of an ad break).
   * Sends the ad_mode_begin event.
   */
  private onAdModeBegin(): void {
    // Send final engagement event for content before ad break starts
    // This is only sent if we're currently in MAIN context
    if (
      this.videoViewTracked &&
      !this.adBreakActive &&
      (!this.isAdComposedPlayer(this.player) || this.player.playerContext === PlayerContext.MAIN)
    ) {
      this.onVideoEngagement(true);
    }
    this.tracker.trackAdModeBegin();
  }

  /**
   * Called when ad playback completes (end of an ad break).
   * Sends the ad_mode_complete event.
   */
  private onAdModeComplete(): void {
    this.tracker.trackAdModeComplete();
    // Reset engagement tracking for post-ad content
    this.lastEngagementTime = this.player.currentTime;
    this.engagementRangeStart = this.player.currentTime;
  }

  /**
   * Called when a playback error occurs.
   * Sends the error event.
   *
   * @param errorCode - The error code or error message
   */
  private onError(errorCode: string | number): void {
    this.tracker.trackError(errorCode);
  }

  /**
   * Gets the current session ID.
   */
  public getSessionId(): string {
    return this.tracker.getSessionId();
  }

  /**
   * Extracts QoE metrics from the player.
   * Only returns metrics that are available from PlaybackStatistics and match the Brightcove API spec.
   */
  private extractQoEMetrics(): {
    renditionUrl?: string;
    renditionBitrate?: number;
    renditionMimeType?: string;
    renditionHeight?: number;
    renditionWidth?: number;
    rebufferingSeconds?: number;
    rebufferingCount?: number;
    forwardBufferSeconds?: number;
    measuredBps?: number;
    mediaBytesTransferred?: number;
    mediaSecondsLoaded?: number;
    playerWidth?: number;
    playerHeight?: number;
    droppedFrames?: number;
  } {
    const metrics: Record<string, any> = {};
    this.extractPlaybackStatisticsMetrics(metrics);
    this.extractActiveVariantMetrics(metrics);
    return metrics;
  }

  /**
   * Extract metrics from playback statistics.
   */
  private extractPlaybackStatisticsMetrics(metrics: Record<string, any>): void {
    try {
      const stats = this.player.playbackStatistics;
      if (!stats) {
        return;
      }

      // Bitrate (measured bandwidth in bps)
      if (stats.estimatedBandwidth) {
        metrics.measuredBps = stats.estimatedBandwidth;
      }

      // Dropped frames
      if (stats.droppedFrames !== undefined && !Number.isNaN(stats.droppedFrames)) {
        metrics.droppedFrames = stats.droppedFrames;
      }

      // Player dimensions
      if (stats.width) metrics.playerWidth = stats.width;
      if (stats.height) metrics.playerHeight = stats.height;
    } catch {
      if (this.config.debugLogging) {
        this.logger.log('[DATA_COLLECTION] Error extracting from playbackStatistics');
      }
    }
  }

  /**
   * Extract active variant information from trackschanged event.
   */
  private extractActiveVariantMetrics(metrics: Record<string, any>): void {
    if (this.activeVariantBitrate) {
      metrics.renditionBitrate = this.activeVariantBitrate;
    }
    if (this.activeVariantMimeType) {
      metrics.renditionMimeType = this.activeVariantMimeType;
    }
    if (this.activeVariantHeight) {
      metrics.renditionHeight = this.activeVariantHeight;
    }
    if (this.activeVariantWidth) {
      metrics.renditionWidth = this.activeVariantWidth;
    }
  }
}
